import {Pressable, Text, View} from 'native-base';
import React, {useEffect, useState} from 'react';
import Icon from 'react-native-vector-icons/FontAwesome5Pro';

import {scrollViewRef} from '../PageTemplate';
import {useTheme} from 'styled-components/native';

const TEST_ID_PREFIX = 'pagination-bar';
const PRESSABLE_SIZE = 32;
const PRESSABLE_SIZE_PX = `${PRESSABLE_SIZE}px`;

/**
 * Shown when page numbers are grouped and hidden
 */
const Ellipsis = props => (
  <View
    width={PRESSABLE_SIZE_PX}
    height={PRESSABLE_SIZE_PX}
    alignItems="center"
    justifyContent="center"
    {...props}>
    <Text selectable={false} fontWeight={700} fontSize="md" color="paleSky">
      ...
    </Text>
  </View>
);

interface calcRangeArgs {
  currentPage: number;
  numPages: number;
}

/**
 * Helper function to determine the range of values as well as ellipsis in the number bar.
 * It is designed to only consider the default behavior of
 */
const calcRange = ({currentPage, numPages}: calcRangeArgs) => {
  let start = 2;
  let end = numPages - 1;
  let startEllipsis = false;
  let endEllipsis = false;

  if (numPages > 7) {
    startEllipsis = currentPage > 4;
    endEllipsis = currentPage < numPages - 3;

    if (currentPage > numPages - 4) {
      start = numPages - 4;
    } else if (currentPage > 4) {
      start = currentPage - 1;
    }

    if (currentPage < numPages - 3) {
      end = start + (startEllipsis ? 2 : 3);
    }
  }

  return {start, end, startEllipsis, endEllipsis};
};

/**
 * Each of the page number pressable
 */
const PageNum = ({children, active, isCompact, ...rest}) => (
  <Pressable
    accessibilityLabel={`Page ${children}`}
    height={PRESSABLE_SIZE_PX}
    width={PRESSABLE_SIZE_PX}
    borderRadius={PRESSABLE_SIZE / 2}
    marginX={isCompact ? 0 : 1}
    backgroundColor={active ? 'caesarRewards' : 'transparent'}
    _hover={{bgColor: active ? 'caesarRewards' : 'silverSand'}}
    {...rest}>
    <View height="100%" alignItems="center" justifyContent="center">
      <Text
        selectable={false}
        fontWeight={700}
        fontSize="md"
        color={active ? '#FFFFFF' : 'paleSky'}>
        {children}
      </Text>
    </View>
  </Pressable>
);

/**
 * Pressable wrappers for the font awesome angle left and right icons
 */
const PageArrow = ({icon, disabled, ...rest}) => (
  <Pressable
    height={PRESSABLE_SIZE_PX}
    width={PRESSABLE_SIZE_PX}
    borderRadius={PRESSABLE_SIZE / 2}
    disabled={disabled}
    _hover={{bgColor: !disabled ? 'silverSand' : 'transparent'}}
    {...rest}>
    <View height="100%" alignItems="center" justifyContent="center">
      {!disabled && icon}
    </View>
  </Pressable>
);

interface PaginationBarProps {
  /**
   * The page the component should select on initial render
   */
  defaultPage?: number;

  /**
   * Flag to set navigation arrows to full width of parent container.
   */
  isFullWidth?: boolean;

  /**
   * Flag to decrease margins for compact or mobile formats.
   */
  isCompact?: boolean;

  /**
   * The total number of pages we should indicate
   */
  numPages: number;

  /**
   * A callback handler to notify the parent component
   * when page button was selected and what page number that was
   */
  onPageChange: (pageNum: number) => void;

  /**
   * A flag to control the the auto-scroll
   */
  shouldScrollToTop?: boolean;
}

/**
 * This component provides a basic pagination bar using numbers and arrows
 */
const PaginationBar = (props: PaginationBarProps) => {
  const {
    numPages,
    onPageChange,
    defaultPage = 1,
    isFullWidth: fullWidth = false,
    isCompact = false,
    shouldScrollToTop = true,
    ...rest
  } = props;

  const theme = useTheme();

  const [currentPage, setCurrentPage] = useState<number>(defaultPage);
  const [range, setRange] = useState<{
    start: number;
    end: number;
    startEllipsis: boolean;
    endEllipsis: boolean;
  }>(calcRange({currentPage, numPages}));

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    shouldScrollToTop && scrollViewRef?.current?.scrollTo();
  };

  useEffect(() => {
    onPageChange && onPageChange(currentPage);
  }, [currentPage, onPageChange]);

  /**
   * Handle resizing by correctly resetting the range when the numPages to be shown changes.
   */
  useEffect(() => {
    if (currentPage > numPages) {
      setCurrentPage(numPages);
    } else {
      setRange(calcRange({currentPage, numPages}));
    }
  }, [numPages, currentPage]);

  const array: number[] = [];
  for (let i = range.start; i <= range.end; i += 1) {
    array.push(i);
  }

  return (
    <View
      flexDirection="row"
      flex={1}
      width={fullWidth ? '100%' : 'auto'}
      justifyContent={fullWidth ? 'space-between' : 'center'}
      {...rest}>
      <PageArrow
        accessibilityLabel="Previous Page"
        disabled={currentPage === 1}
        onPress={() => handlePageChange(currentPage - 1)}
        testID={`${TEST_ID_PREFIX}-back`}
        marginRight={fullWidth ? 0 : '64px'}
        icon={
          <Icon
            selectable={false}
            name="angle-left"
            testID={`${TEST_ID_PREFIX}-back-icon`}
            size={16}
            color={theme.colors.paleSky}
          />
        }
      />
      <View flexDirection="row" flex={1} justifyContent="center">
        <PageNum
          isCompact={isCompact}
          testID={`${TEST_ID_PREFIX}-num-1`}
          active={currentPage === 1}
          onPress={() => handlePageChange(1)}>
          1
        </PageNum>
        {range.startEllipsis && (
          <Ellipsis testID={`${TEST_ID_PREFIX}-start-ellipsis`} />
        )}
        {array.map((i, idx) => (
          <PageNum
            isCompact={isCompact}
            key={`PaginationBar-Number-${i}-${idx}`}
            testID={`${TEST_ID_PREFIX}-num-${i}`}
            active={currentPage === i}
            onPress={() => handlePageChange(i)}>
            {i}
          </PageNum>
        ))}
        {range.endEllipsis && (
          <Ellipsis testID={`${TEST_ID_PREFIX}-end-ellipsis`} />
        )}
        <PageNum
          isCompact={isCompact}
          testID={`${TEST_ID_PREFIX}-num-${numPages}`}
          active={currentPage === numPages}
          onPress={() => handlePageChange(numPages)}>
          {numPages}
        </PageNum>
      </View>
      <PageArrow
        accessibilityLabel="Next Page"
        disabled={currentPage === numPages}
        onPress={() => handlePageChange(currentPage + 1)}
        testID={`${TEST_ID_PREFIX}-forward`}
        marginLeft={fullWidth ? 0 : '64px'}
        icon={
          <Icon
            selectable={false}
            name="angle-right"
            size={16}
            testID={`${TEST_ID_PREFIX}-forward-icon`}
            color={theme.colors.paleSky}
          />
        }
      />
    </View>
  );
};

export default PaginationBar;
